import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, 
  Loader2,
  Search,
  Filter,
  User,
  Building2,
  Wallet,
  Clock
} from 'lucide-react';

const AdminAuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
  });

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      
      const response = await api.get('/admin/audit-logs', { params });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      user: User,
      opportunity: Building2,
      property: Building2,
      commitment: Wallet,
      document: FileText,
    };
    return icons[entityType] || FileText;
  };

  const getActionBadge = (action) => {
    const styles = {
      created: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      updated: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      deleted: 'bg-red-500/10 text-red-500 border-red-500/20',
      approved: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
      login: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    };
    
    // Find matching style based on action containing keyword
    let style = 'bg-muted text-muted-foreground border-muted';
    for (const [key, value] of Object.entries(styles)) {
      if (action.toLowerCase().includes(key)) {
        style = value;
        break;
      }
    }
    
    return <Badge variant="outline" className={style}>{action}</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="admin-audit-log-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Registro de Auditoría</h1>
          <p className="body-text">{total} eventos registrados.</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select
              value={filters.entity_type}
              onValueChange={(v) => setFilters(prev => ({ ...prev, entity_type: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo de entidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las entidades</SelectItem>
                <SelectItem value="user">Usuarios</SelectItem>
                <SelectItem value="opportunity">Oportunidades</SelectItem>
                <SelectItem value="property">Inmuebles</SelectItem>
                <SelectItem value="commitment">Compromisos</SelectItem>
                <SelectItem value="document">Documentos</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={fetchLogs}
              className="gap-2"
            >
              <Filter className="h-4 w-4" strokeWidth={1.5} />
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      ) : logs.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground">No hay registros de auditoría</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-dashboard">
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {logs.map((log) => {
                const Icon = getEntityIcon(log.entity_type);
                return (
                  <div key={log.log_id} className="p-4 hover:bg-muted/30 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-muted rounded-sm">
                        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {getActionBadge(log.action)}
                          <span className="text-sm font-medium capitalize">{log.entity_type}</span>
                          {log.entity_id && (
                            <span className="text-xs text-muted-foreground font-mono">
                              {log.entity_id}
                            </span>
                          )}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <p className="text-xs text-muted-foreground truncate">
                            {JSON.stringify(log.details)}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" strokeWidth={1.5} />
                            {new Date(log.created_at).toLocaleString('es-MX')}
                          </span>
                          {log.user_id && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" strokeWidth={1.5} />
                              {log.user_id}
                            </span>
                          )}
                          {log.ip_address && (
                            <span>IP: {log.ip_address}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAuditLogPage;
